import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Edit2, X, Upload, Star, ImagePlus, ExternalLink, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Modal from "@/components/site/Modal";

const EMPTY = {
  id: null,
  name: "",
  tagline: "",
  bio: "",
  image: "",
  spotify: "",
  apple_music: "",
  youtube: "",
  instagram: "",
  facebook: "",
  twitter: "",
  gallery: [],
  videos: [],
  interviews: [],
  featured: true,
};

/** Extract YouTube ID from URL or raw ID */
function ytId(s) {
  if (!s) return null;
  const trimmed = s.trim();
  // already an ID
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) return trimmed;
  const m = trimmed.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

function slugifyClient(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function ArtistsAdmin() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mainFileRef = useRef(null);
  const galleryFileRef = useRef(null);

  const load = () => api.get("/artists").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const reset = () => setForm(EMPTY);

  const openNew = () => { reset(); setOpen(true); };
  const openEdit = (a) => {
    setForm({
      id: a.id,
      name: a.name || "",
      tagline: a.tagline || "",
      bio: a.bio || "",
      image: a.image || "",
      spotify: a.spotify || "",
      apple_music: a.apple_music || "",
      youtube: a.youtube || "",
      instagram: a.instagram || "",
      facebook: a.facebook || "",
      twitter: a.twitter || "",
      gallery: a.gallery || [],
      videos: a.videos || [],
      interviews: a.interviews || [],
      featured: a.featured ?? true,
    });
    setOpen(true);
  };

  const slug = form.name ? slugifyClient(form.name) : "new-artist";

  const upload = async (files, target) => {
    if (!files || files.length === 0) return;
    if (!form.name) {
      toast.error("Set the artist name first — that determines where files are stored.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const r = await api.post(`/admin/artists/upload?slug=${encodeURIComponent(slug)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const urls = r.data.urls || [];
      if (target === "main") {
        setForm((f) => ({ ...f, image: urls[0] }));
      } else if (target === "gallery") {
        setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...urls] }));
      }
      toast.success(`${urls.length} photo${urls.length === 1 ? "" : "s"} uploaded`);
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryAt = (i) => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }));

  /** Pasted YT links → IDs */
  const addVideos = (raw, key) => {
    const lines = raw.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    const ids = lines.map(ytId).filter(Boolean);
    setForm((f) => ({ ...f, [key]: [...new Set([...(f[key] || []), ...ids])] }));
  };
  const removeFromList = (key, id) => setForm((f) => ({ ...f, [key]: f[key].filter((x) => x !== id) }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      if (form.id) {
        await api.put(`/admin/artists/${form.id}`, payload);
        toast.success("Artist updated");
      } else {
        await api.post("/admin/artists", payload);
        toast.success("Artist created");
      }
      setOpen(false);
      reset();
      load();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/artists/${a.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  /* ---- Drag-to-reorder ---- */
  const dragId = useRef(null);
  const [overId, setOverId] = useState(null);

  const onDragStart = (e, id) => {
    dragId.current = id;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch {}
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    if (id !== overId) setOverId(id);
  };
  const onDragEnd = () => { dragId.current = null; setOverId(null); };
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const fromId = dragId.current;
    setOverId(null);
    dragId.current = null;
    if (!fromId || fromId === targetId) return;

    const next = [...rows];
    const fromIdx = next.findIndex((r) => r.id === fromId);
    const toIdx = next.findIndex((r) => r.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setRows(next); // optimistic

    try {
      await api.post("/admin/artists/reorder", { ids: next.map((r) => r.id) });
    } catch (err) {
      toast.error("Reorder failed — reverting");
      load();
    }
  };

  return (
    <div data-testid="admin-artists">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label-mono mb-2">/ Roster</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter">Artists</h1>
          <p className="text-zinc-500 text-sm mt-2">{rows.length} artist{rows.length === 1 ? "" : "s"} · drag the handle to reorder</p>
        </div>
        <button onClick={openNew} className="glass-btn solid" data-testid="artist-new"><Plus size={14}/> New Artist</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {rows.map((a) => (
          <div
            key={a.id}
            draggable
            onDragStart={(e) => onDragStart(e, a.id)}
            onDragOver={(e) => onDragOver(e, a.id)}
            onDrop={(e) => onDrop(e, a.id)}
            onDragEnd={onDragEnd}
            className={`relative rounded-2xl border bg-[#121215] overflow-hidden lift group transition-all ${overId === a.id ? "border-cyan-400/60 ring-2 ring-cyan-400/30" : "border-white/5"}`}
            data-testid={`artist-row-${a.id}`}
          >
            <div
              className="absolute top-3 left-3 z-10 w-8 h-8 rounded-full bg-black/70 backdrop-blur border border-white/10 flex items-center justify-center text-zinc-400 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
              title="Drag to reorder"
              aria-label="Drag handle"
              data-testid={`artist-drag-${a.id}`}
            >
              <GripVertical size={14} />
            </div>
            {a.image ? (
              <img src={a.image} alt={a.name} className="w-full h-56 object-cover pointer-events-none" />
            ) : (
              <div className="w-full h-56 bg-[#0a0a0c] flex items-center justify-center text-zinc-700">
                <ImagePlus size={32} />
              </div>
            )}
            <div className="p-5">
              <div className="flex items-center gap-2">
                <div className="font-display font-bold text-lg flex-1 truncate">{a.name}</div>
                {a.featured && <Star size={14} className="text-cyan-400 fill-cyan-400 shrink-0" />}
              </div>
              <div className="label-mono text-cyan-300/80 mt-1 truncate">{a.tagline}</div>
              <div className="flex flex-wrap gap-3 text-[11px] text-zinc-500 mt-3 font-mono">
                <span>{(a.gallery || []).length} photos</span>
                <span>{(a.videos || []).length} videos</span>
                <span>{(a.interviews || []).length} interviews</span>
              </div>
              <div className="flex items-center justify-between mt-4 gap-2">
                <a
                  href={`/${a.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-500 hover:text-cyan-300 text-xs font-mono inline-flex items-center gap-1"
                  data-testid={`artist-view-${a.id}`}
                >
                  /{a.slug} <ExternalLink size={11} />
                </a>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(a)} className="glass-btn ghost p-2" aria-label="Edit" data-testid={`artist-edit-${a.id}`}>
                    <Edit2 size={14}/>
                  </button>
                  <button onClick={() => del(a)} className="glass-btn ghost p-2" aria-label="Delete" data-testid={`artist-del-${a.id}`}>
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        wide
        title={form.id ? `Edit · ${form.name}` : "New Artist"}
        testid="artist-modal"
      >
        <form onSubmit={save} className="space-y-6" data-testid="artist-form">
          {/* IDENTITY */}
          <Section label="Identity">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Name *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="ar-name"/>
              <Input label="Tagline" placeholder="Hip-Hop / Pop · Los Angeles" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testid="ar-tagline"/>
            </div>
            <div>
              <label className="label-mono block mb-2">Bio (multi-paragraph supported — separate paragraphs with blank lines)</label>
              <textarea
                className="pulse-input pulse-textarea"
                rows={8}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                data-testid="ar-bio"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="ar-featured"/>
              Featured artist (appears on home + roster top section)
            </label>
          </Section>

          {/* MAIN PHOTO */}
          <Section label="Main Photo">
            <div className="flex gap-4 items-start">
              {form.image ? (
                <div className="relative shrink-0 w-40 h-52 rounded-xl overflow-hidden border border-white/10">
                  <img src={form.image} alt="" className="w-full h-full object-cover"/>
                  <button type="button" onClick={() => setForm({ ...form, image: "" })} className="absolute top-2 right-2 bg-black/70 hover:bg-black p-1 rounded-full" aria-label="Remove main photo">
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="w-40 h-52 rounded-xl border border-dashed border-white/15 bg-[#0a0a0c] flex items-center justify-center text-zinc-600">
                  <ImagePlus size={28}/>
                </div>
              )}
              <div className="flex-1 space-y-3">
                <input
                  ref={mainFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => upload(e.target.files, "main")}
                  data-testid="ar-main-file"
                />
                <button type="button" onClick={() => mainFileRef.current?.click()} disabled={uploading} className="glass-btn ghost" data-testid="ar-main-upload">
                  <Upload size={14}/> {uploading ? "Uploading..." : "Upload main photo"}
                </button>
                <Input label="…or paste an image URL" value={form.image} onChange={(v) => setForm({ ...form, image: v })} placeholder="/api/uploads/... or https://..." testid="ar-image-url"/>
              </div>
            </div>
          </Section>

          {/* GALLERY */}
          <Section label="Photo Gallery">
            <div className="flex flex-wrap gap-3">
              {form.gallery.map((url, i) => (
                <div key={url + i} className="relative w-28 h-36 rounded-lg overflow-hidden border border-white/10" data-testid={`ar-gallery-${i}`}>
                  <img src={url} alt="" className="w-full h-full object-cover"/>
                  <button type="button" onClick={() => removeGalleryAt(i)} className="absolute top-1 right-1 bg-black/70 hover:bg-black p-1 rounded-full" aria-label="Remove">
                    <X size={10} />
                  </button>
                </div>
              ))}
              <input
                ref={galleryFileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => upload(e.target.files, "gallery")}
                data-testid="ar-gallery-file"
              />
              <button
                type="button"
                onClick={() => galleryFileRef.current?.click()}
                disabled={uploading}
                className="w-28 h-36 rounded-lg border border-dashed border-white/15 hover:border-cyan-400/40 hover:bg-cyan-400/5 flex flex-col items-center justify-center gap-1 text-zinc-500 hover:text-cyan-300 transition-colors text-xs"
                data-testid="ar-gallery-add"
              >
                <Upload size={16}/> Add photos
              </button>
            </div>
          </Section>

          {/* MUSIC VIDEOS */}
          <Section label="Music Videos">
            <YTPaster
              testid="ar-videos"
              onAdd={(raw) => addVideos(raw, "videos")}
              placeholder="Paste YouTube URLs (one per line or comma-separated)..."
            />
            <ChipList items={form.videos} testid="ar-video-list" onRemove={(id) => removeFromList("videos", id)} />
          </Section>

          {/* INTERVIEWS */}
          <Section label="Press &amp; Interviews">
            <YTPaster
              testid="ar-interviews"
              onAdd={(raw) => addVideos(raw, "interviews")}
              placeholder="Paste YouTube interview URLs..."
            />
            <ChipList items={form.interviews} testid="ar-interview-list" onRemove={(id) => removeFromList("interviews", id)} />
          </Section>

          {/* STREAMING */}
          <Section label="Streaming">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Spotify" value={form.spotify} onChange={(v) => setForm({ ...form, spotify: v })} placeholder="https://open.spotify.com/artist/..." testid="ar-spotify"/>
              <Input label="Apple Music" value={form.apple_music} onChange={(v) => setForm({ ...form, apple_music: v })} placeholder="https://music.apple.com/..." testid="ar-apple"/>
              <Input label="YouTube channel" value={form.youtube} onChange={(v) => setForm({ ...form, youtube: v })} placeholder="https://www.youtube.com/@..." testid="ar-youtube"/>
            </div>
          </Section>

          {/* SOCIALS */}
          <Section label="Socials">
            <div className="grid sm:grid-cols-3 gap-3">
              <Input label="Instagram" value={form.instagram} onChange={(v) => setForm({ ...form, instagram: v })} placeholder="https://instagram.com/..." testid="ar-instagram"/>
              <Input label="Facebook" value={form.facebook} onChange={(v) => setForm({ ...form, facebook: v })} placeholder="https://facebook.com/..." testid="ar-facebook"/>
              <Input label="Twitter / X" value={form.twitter} onChange={(v) => setForm({ ...form, twitter: v })} placeholder="https://twitter.com/..." testid="ar-twitter"/>
            </div>
          </Section>

          <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-[#0a0a0c]/95 backdrop-blur border-t border-white/5">
            <button type="submit" disabled={saving || uploading} className="glass-btn solid w-full justify-center" data-testid="ar-save">
              {saving ? "Saving..." : (form.id ? "Update Artist" : "Create Artist")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="space-y-3">
      <div className="label-mono text-zinc-500 border-b border-white/5 pb-2">/ {label}</div>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, placeholder = "", testid }) {
  return (
    <div>
      <label className="label-mono block mb-2">{label}</label>
      <input
        className="pulse-input"
        type={type}
        required={required}
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}

function YTPaster({ onAdd, placeholder, testid }) {
  const [val, setVal] = useState("");
  const submit = () => { if (val.trim()) { onAdd(val); setVal(""); } };
  return (
    <div className="flex gap-2">
      <textarea
        className="pulse-input pulse-textarea flex-1"
        rows={2}
        placeholder={placeholder}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        data-testid={`${testid}-input`}
      />
      <button type="button" onClick={submit} className="glass-btn shrink-0 self-stretch" data-testid={`${testid}-add`}>
        <Plus size={14}/> Add
      </button>
    </div>
  );
}

function ChipList({ items, onRemove, testid }) {
  if (!items || items.length === 0) {
    return <div className="text-zinc-600 text-sm italic">No videos yet.</div>;
  }
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {items.map((id) => (
        <a
          key={id}
          href={`https://youtu.be/${id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg bg-[#0a0a0c] border border-white/10 hover:border-cyan-400/40 transition-colors"
        >
          <img
            src={`https://i.ytimg.com/vi/${id}/default.jpg`}
            alt=""
            className="w-12 h-9 object-cover rounded"
            loading="lazy"
          />
          <span className="text-xs font-mono text-zinc-400 group-hover:text-cyan-300">{id}</span>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); onRemove(id); }}
            className="ml-1 text-zinc-600 hover:text-red-400"
            aria-label="Remove"
          >
            <X size={12}/>
          </button>
        </a>
      ))}
    </div>
  );
}
